/**
 * The hub card: the fridge door, at a glance.
 *
 * Composer first, then the last few notes — on a phone in a kitchen, the point is
 * to say something in one tap, not to scroll. Prop-free on purpose: the integrator
 * drops <MessageBoardCard /> onto the home screen and it feeds itself.
 *
 * Day one it is EMPTY, and empty is the state most people will meet first, so it
 * gets a real invitation instead of a shrug.
 */
import { Link } from 'react-router-dom';
import { useIdentity } from '../../lib/identity';
import Composer from './Composer';
import MessageItem, { PendingItem } from './MessageItem';
import { ErrorState, LoadingMessages, useNow } from './shared';
import { useBoard } from './useBoard';

const SHOW = 3;

export default function MessageBoardCard() {
  const me = useIdentity();
  const now = useNow();
  const { messages, pending, total, loading, error, reload, post, edit, remove } = useBoard(10);

  const recent = messages.slice(0, SHOW);

  // How many notes are on the board that this card is NOT showing.
  //
  // This CANNOT come from `messages.length` — that list is a truncated page, so it
  // could only ever count up to the page size and would tell the family "7 more notes"
  // about a board with fifty on it. It comes from the API's real COUNT, and when that
  // is unknown (`total === null`) we show no number rather than a made-up one.
  const more = total != null && total > recent.length ? total - recent.length : null;

  // We may not know HOW MANY are hidden, but a page longer than the card is proof
  // that some are. Then the link stands on its own, without a number.
  const hasMore = more != null || messages.length > recent.length;

  return (
    <section className="rounded-card border border-border bg-surface p-5 shadow-[0_1px_3px_rgba(43,39,35,0.05)] sm:p-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold tracking-wide text-ink-soft uppercase">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.6}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4 text-ink-faint"
            aria-hidden="true"
          >
            <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v9A1.5 1.5 0 0 1 18.5 16H9l-5 4V5.5Z" />
            <path d="M8 8.5h8M8 12h5" />
          </svg>
          Family board
        </h2>
        <Link
          to="/board"
          className="-mr-2 inline-flex min-h-11 items-center rounded-full px-2 text-sm font-medium text-ink-soft hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
        >
          Open board
        </Link>
      </div>

      <div className="mt-4">
        <Composer me={me} onPost={post} rows={2} />
      </div>

      <div className="mt-5 border-t border-border pt-5">
        {loading && <LoadingMessages rows={2} />}

        {/* A failed read is never rendered as an empty board. */}
        {!loading && error && <ErrorState message={error} onRetry={reload} />}

        {!loading && !error && messages.length === 0 && pending.length === 0 && (
          <div className="text-center">
            <p className="font-display text-lg font-semibold text-ink">The board is empty.</p>
            <p className="mx-auto mt-1 max-w-xs text-sm leading-relaxed text-ink-soft">
              Be the first. A reminder, a thank-you, a bad joke — everybody in the house sees it.
            </p>
          </div>
        )}

        {!loading && !error && (messages.length > 0 || pending.length > 0) && (
          <>
            <ul className="space-y-5">
              {pending.map((p) => (
                <PendingItem key={p.tempId} me={me} body={p.body} />
              ))}
              {recent.map((m) => (
                <MessageItem
                  key={m.id}
                  message={m}
                  me={me}
                  now={now}
                  onEdit={edit}
                  onDelete={remove}
                />
              ))}
            </ul>

            {hasMore && (
              <Link
                to="/board"
                className="mt-4 inline-flex min-h-11 items-center gap-1.5 rounded-full px-1 text-sm font-medium text-sage hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
              >
                {more == null
                  ? 'See the whole board'
                  : `${more === 1 ? '1 more note' : `${more} more notes`} on the board`}
                <span aria-hidden="true">→</span>
              </Link>
            )}
          </>
        )}
      </div>
    </section>
  );
}
